import { useEffect, useState } from "react";
import { validateLatex } from "../../utils/latexValidator";

import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack,
  TextField,
  Typography,
  MenuItem,
  Chip,
} from "@mui/material";

import api from "../../utils/api";
import ImageUploadField from "./ImageUploadField";

/**
 * EditQuestionDialog — FINAL, SAFE VERSION
 *
 * DATA CONTRACT:
 * - stem_image_url           → Cloudinary PUBLIC_ID (saved to DB)
 * - _stem_image_preview      → Cloudinary FULL URL (preview only)
 */
export default function EditQuestionDialog({
  open,
  onClose,
  question,
  onSaved,
}) {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  // ------------------------------------
  // INIT FORM FROM QUESTION (CRITICAL)
  // ------------------------------------
  useEffect(() => {
    if (question) {
      setForm({
        id: question.id,
        question: question.question,
        option_a: question.option_a,
        option_b: question.option_b,
        option_c: question.option_c,
        option_d: question.option_d,
        correct_option: question.correct_option,
        explanation: question.explanation || "",
        difficulty: question.difficulty || "medium",

        // 🔑 PUBLIC_ID → used for saving to DB
        stem_image_url: question._stem_image_public_id || null,

        // 🖼️ FULL URL → used ONLY for preview
        _stem_image_preview: question.stem_image_url || null,
      });
    } else {
      setForm(null);
    }
  }, [question]);

  const update = (k, v) =>
    setForm((f) => ({
      ...f,
      [k]: v,
    }));

  // ------------------------------------
  // SAVE HANDLER
  // ------------------------------------
  const handleSave = async () => {
    if (!form) return;

    // ===============================
    // 🛑 LaTeX VALIDATION
    // ===============================
    const fieldsToValidate = [
      { label: "Question", value: form.question },
      { label: "Option A", value: form.option_a },
      { label: "Option B", value: form.option_b },
      { label: "Option C", value: form.option_c },
      { label: "Option D", value: form.option_d },
      { label: "Explanation", value: form.explanation },
    ];

    for (const field of fieldsToValidate) {
      const errors = validateLatex(field.value);
      if (errors) {
        alert(
          `LaTeX error in ${field.label}:\n` +
            errors.map((e) => `• ${e}`).join("\n")
        );
        return;
      }
    }

    // ===============================
    // SAVE (STRIP PREVIEW FIELD)
    // ===============================
    setSaving(true);
    try {
      const { _stem_image_preview, ...payload } = form;

      const { data } = await api.put(
        `/admin/questions/${form.id}`,
        payload
      );

      onSaved(data.question);
      onClose();
    } finally {
      setSaving(false);
    }
  };

  if (!form) return null;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>Edit Question</DialogTitle>

      <DialogContent dividers>
        <Stack spacing={2}>
          <TextField
            label="Question"
            value={form.question}
            onChange={(e) =>
              update("question", e.target.value)
            }
            multiline
            minRows={3}
          />

          {/* ============================
              IMAGE UPLOAD (FINAL LOGIC)
          ============================ */}
          <ImageUploadField
            label="Question Image"
            value={form._stem_image_preview} // FULL URL ONLY
            onChange={(publicId) => {
              // user uploaded a NEW image
              update("stem_image_url", publicId);
              update("_stem_image_preview", null);
            }}
          />

          <Typography variant="subtitle2">Options</Typography>

          {["a", "b", "c", "d"].map((l) => (
            <TextField
              key={l}
              label={`Option ${l.toUpperCase()}`}
              value={form[`option_${l}`]}
              onChange={(e) =>
                update(`option_${l}`, e.target.value)
              }
            />
          ))}

          <Typography variant="subtitle2">
            Correct Option
          </Typography>

          <Stack direction="row" spacing={1}>
            {["A", "B", "C", "D"].map((l) => (
              <Chip
                key={l}
                label={l}
                color={
                  form.correct_option === l
                    ? "primary"
                    : "default"
                }
                onClick={() =>
                  update("correct_option", l)
                }
              />
            ))}
          </Stack>

          <TextField
            label="Explanation"
            value={form.explanation}
            onChange={(e) =>
              update("explanation", e.target.value)
            }
            multiline
            minRows={2}
          />

          <TextField
            label="Difficulty"
            select
            value={form.difficulty}
            onChange={(e) =>
              update("difficulty", e.target.value)
            }
          >
            <MenuItem value="easy">Easy</MenuItem>
            <MenuItem value="medium">Medium</MenuItem>
            <MenuItem value="hard">Hard</MenuItem>
          </TextField>
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          disabled={saving}
          onClick={handleSave}
          variant="contained"
        >
          {saving ? "Saving..." : "Save Changes"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
